import { DialogRef } from '@angular/cdk/dialog';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { Router } from '@angular/router';

import type { ErrorInfo } from './error.types';

import { ERROR_STORAGE_KEY, parseBrowserInfo } from './error.types';

/**
 * 错误页面组件
 * 显示错误详情、浏览器信息，并提供用户操作选项
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule],
  selector: 'eliboo-error-page',
  styleUrl: './error-page.component.css',
  template: `
    <div class="error-container">
      <!-- 错误标题 -->
      <div class="error-header">
        <p class="error-subtitle">很抱歉，应用遇到了一个意外错误</p>
      </div>

      <!-- 错误信息卡片 -->
      <div class="error-card">
        <div class="error-section">
          <h3>🐛 错误信息</h3>
          <div class="error-message">{{ errorInfo().message }}</div>
        </div>

        <div class="error-section">
          <h3>🌐 浏览器信息</h3>
          <div class="browser-info">
            <div class="info-row">
              <span class="info-label">浏览器:</span>
              <span class="info-value">{{ browserInfo().name }} {{ browserInfo().version }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">操作系统:</span>
              <span class="info-value">{{ browserInfo().os }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">时间：</span>
              <span class="info-value">{{ formattedTime() }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">页面地址:</span>
              <span class="info-value">{{ errorInfo().url }}</span>
            </div>
          </div>
        </div>

        <!-- 详细信息 -->
        <div class="error-section">
          <h3>🔍 详细信息</h3>
          <div class="error-details">
            <pre class="error-stack">{{ errorInfo().stack || '无堆栈信息' }}</pre>
            @if (errorInfo().context) {
              <div class="error-context">
                <h4>上下文信息:</h4>
                <pre>{{ contextJson() }}</pre>
              </div>
            }
          </div>
        </div>
      </div>

      <!-- 操作按钮 -->
      <div class="error-actions">
        <button class="btn btn-primary" (click)="goHome()">返回</button>
      </div>

      <!-- 帮助信息 -->
      <div class="error-help">
        <h4>💡 解决建议</h4>
        <ul>
          <li>尝试刷新页面或返回首页</li>
          <li>检查网络连接是否正常</li>
          <li>如果问题持续存在，请截图联系技术支持</li>
        </ul>
      </div>
    </div>
  `,
})
export class ErrorPageComponent {
  private router = inject(Router);
  dialogRef = inject(DialogRef, { optional: true });

  // 输入属性：直接传入的错误信息（用于组件模式）
  errorInfoInput = input<ErrorInfo | null>(null); // TODO 从弹窗data中获取

  // 关闭弹窗的输出事件
  closeDialog = output<void>();

  // 计算属性
  errorInfo = computed(() => {
    // 优先使用直接传入的错误信息
    const inputError = this.errorInfoInput();
    if (inputError) {
      return inputError;
    }

    // 回退到从sessionStorage读取（用于路由模式）
    try {
      const stored = sessionStorage.getItem(ERROR_STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored) as ErrorInfo;
      }
    } catch (e) {
      console.warn('Failed to parse stored error info:', e);
    }

    // 默认错误信息
    return {
      message: '未知错误',
      stack: '',
      timestamp: new Date().toISOString(),
      type: 'unknown' as any,
      url: window.location.href,
      userAgent: navigator.userAgent,
    } as ErrorInfo;
  });

  browserInfo = computed(() => parseBrowserInfo());

  formattedTime = computed(() => {
    try {
      return new Date(this.errorInfo().timestamp).toLocaleString('zh-CN', {
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        month: '2-digit',
        second: '2-digit',
        year: 'numeric',
      });
    } catch {
      return '时间解析失败';
    }
  });

  contextJson = computed(() => {
    try {
      return JSON.stringify(this.errorInfo().context, null, 2);
    } catch {
      return '上下文信息解析失败';
    }
  });

  /**
   * 返回首页
   */
  goHome(): void {
    try {
      sessionStorage.removeItem(ERROR_STORAGE_KEY);
      if (this.dialogRef) {
        this.dialogRef.close();
      } else {
        this.router.navigate(['/']);
      }
    } catch (error) {
      console.error('Failed to navigate home:', error);
      window.location.href = '/';
    }
  }
}
